
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { DrawingHistorySidebar } from "./DrawingHistorySidebar";
import { usePremiumFeatures } from "@/hooks/use-premium-features";
import { useAuth } from "@/components/AuthProvider";
import { useState } from "react";
import { LoadDrawingConfirmDialog } from "./LoadDrawingConfirmDialog";
import { CustomCanvas } from "@/types/fabric";

interface DrawingHistorySheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onLoadDrawing: (drawingData: string, drawingId: string, analysisData?: any) => void;
  onRefreshNeeded?: (refreshFn: () => void) => void;
  canvas: CustomCanvas | null;
}

export function DrawingHistorySheet({
  open,
  onOpenChange,
  onLoadDrawing,
  onRefreshNeeded,
  canvas
}: DrawingHistorySheetProps) {
  const { isAuthenticated, handlePremiumFeature } = usePremiumFeatures();
  const { user } = useAuth();
  const [pendingDrawingId, setPendingDrawingId] = useState<string | null>(null);
  const [pendingDrawingData, setPendingDrawingData] = useState<string | null>(null);
  const [pendingAnalysisData, setPendingAnalysisData] = useState<any | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const handleOpenChange = (isOpen: boolean) => {
    // Prevent opening if not authenticated
    if (isOpen && !user) {
      return;
    }
    
    if (isOpen && !handlePremiumFeature('drawing_history')) {
      return;
    }

    console.log('DrawingHistorySheet: handleOpenChange', isOpen);
    onOpenChange(isOpen);
  };

  const handleLoadDrawing = (drawingData: string, drawingId: string, analysisData?: any) => {
    // Check if there are unsaved changes
    const hasUnsavedChanges = canvas && (canvas.data?.isModified === true);
    
    if (hasUnsavedChanges) {
      // Store the drawing details for later use
      setPendingDrawingId(drawingId);
      setPendingDrawingData(drawingData);
      setPendingAnalysisData(analysisData || null);
      setShowConfirmDialog(true);
    } else {
      // No unsaved changes, load directly
      onLoadDrawing(drawingData, drawingId, analysisData);
      // Close sheet after loading
      onOpenChange(false);
    }
  };

  const handleConfirmLoad = () => {
    // Load the drawing after user confirmed
    if (pendingDrawingData && pendingDrawingId) {
      onLoadDrawing(pendingDrawingData, pendingDrawingId, pendingAnalysisData);
      // Reset pending state
      setPendingDrawingId(null);
      setPendingDrawingData(null);
      setPendingAnalysisData(null);
      // Close sheet after loading
      onOpenChange(false);
    }
    setShowConfirmDialog(false);
  };

  return (
    <>
      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetContent 
          side="right" 
          className={cn(
            "w-[50%] p-0",
            "fixed top-1/2 -translate-y-1/2 right-6", 
            "max-h-[80vh] overflow-y-auto",
            "bg-background/95 backdrop-blur-lg",
            "rounded-xl border shadow-lg",
            "pointer-events-auto",
            "!mr-0" // Remove margin from right side
          )}
        >
          <DrawingHistorySidebar 
            onLoadDrawing={handleLoadDrawing}
            onRefreshNeeded={onRefreshNeeded}
          />
        </SheetContent>
      </Sheet>

      <LoadDrawingConfirmDialog
        open={showConfirmDialog}
        onOpenChange={setShowConfirmDialog}
        onConfirm={handleConfirmLoad}
      />
    </>
  );
}
